import {
  cardBattleChances,
  cardDraw,
  initializeCards,
  supportCardName,
  type CardChoice,
  type CardScore,
  type FactionId,
  type GameContext,
  type GameState,
  type PieceId,
} from '@archon3d/core';

/**
 * A Card clash as it is told to the people in it (audit step 18b: the owner found the clash never
 * really showed its result, nor any card moving). The fighters face each other with their chances
 * alone; each side's support is laid down in turn, moving the chances; the chances are rolled; the
 * result is named. The telling takes a few seconds and is the same on the screen and in the headset.
 */
export interface CardStory {
  /** The fighters in the order they are shown, left to right, and the chances bar with them. */
  order: PieceId[];
  /** Each fighter's chance, by `order`: with no support played, then after each one laid down. */
  odds: number[][];
  /** Where the draw that decided the clash falls along the bar: 0 at its left end, 1 at its right. */
  draw: number;
  /** How far the telling has got (see `storyEnd`): the fighters alone, each support, the roll, the result. */
  step: number;
}

/** The step at which each support is down, the roll, and the result: supports 1…n, n + 1, n + 2. */
export const storyEnd = (story: Pick<CardStory, 'order'>): number => story.order.length + 2;
export const rollStep = (story: Pick<CardStory, 'order'>): number => story.order.length + 1;

/** How long, in ms, the telling holds each step before the next: slow enough to follow. */
export const STORY_PACE = {
  /** The fighters alone, before the first support comes down. */
  opening: 900,
  /** Between one support and the next. */
  support: 800,
  /** The last support down, before the roll. */
  settle: 700,
  /** The roll itself, the needle running along the chances before it stops. */
  roll: 1600,
} as const;

/** How long the telling waits in `step` before it moves to the next. */
export function msAfterStep(story: Pick<CardStory, 'order'>, step: number): number {
  const n = story.order.length;
  if (step === 0) return STORY_PACE.opening;
  if (step < n) return STORY_PACE.support;
  if (step === n) return STORY_PACE.settle;
  return STORY_PACE.roll;
}

/**
 * The telling of a clash from the state it was resolved from and its scores. The chances after
 * each support are the core's own (`cardBattleChances`), the last row is the scores' (what decided
 * it), and the draw is the one the core made (`cardDraw`), placed in the winner's share of the bar.
 */
export function tellCardClash(
  ctx: GameContext,
  before: GameState,
  scores: readonly CardScore[],
  order: readonly PieceId[],
): CardStory {
  const dealt = initializeCards(ctx, before);
  const shown = order.map((id) => scores.find((s) => s.pieceId === id)!);
  const chances = (choices: Partial<Record<FactionId, CardChoice>>) => {
    const all = cardBattleChances(ctx, dealt, choices);
    return order.map((id) => all.find((s) => s.pieceId === id)?.probability ?? 0);
  };
  const odds = [chances({})];
  const played: Partial<Record<FactionId, CardChoice>> = {};
  for (const score of shown) {
    played[score.faction] = { cardId: score.cardId, ...(score.role ? { role: score.role } : {}) };
    odds.push(chances(played));
  }
  odds[odds.length - 1] = shown.map((s) => s.probability ?? 0);
  return { order: [...order], odds, draw: drawAlongBar(ctx, before, scores, shown), step: 0 };
}

/**
 * The core draws along the chances in piece-id order; the bar shows them in the telling's order.
 * The draw keeps its place within the winner's share. A draw that does not fall in the winner's
 * share, as for a client out of step with its server, points at the middle of it instead.
 */
function drawAlongBar(
  ctx: GameContext,
  before: GameState,
  scores: readonly CardScore[],
  shown: readonly CardScore[],
): number {
  const winner = scores.find((s) => s.won) ?? shown[0]!;
  const share = winner.probability ?? 0;
  const startIn = (list: readonly CardScore[]) => {
    let from = 0;
    for (const s of list) {
      if (s.pieceId === winner.pieceId) return from;
      from += s.probability ?? 0;
    }
    return from;
  };
  const byPiece = [...scores].sort((a, b) => a.pieceId - b.pieceId);
  let within = cardDraw(ctx, before) - startIn(byPiece);
  if (!(within >= 0 && within < share)) within = share / 2;
  return Math.min(1, Math.max(0, startIn(shown) + within));
}

/** The line a told clash ends on: "Knight beat Goblin", or the winner of a clash of more. */
export function storyHeadline(
  participants: readonly { pieceId: PieceId; name: string }[],
  scores: readonly CardScore[],
): { title: string; note: string } {
  const winner = scores.find((s) => s.won);
  const name = (id: PieceId | undefined) => participants.find((p) => p.pieceId === id)?.name ?? '';
  if (!winner) return { title: 'The clash is decided', note: '' };
  const losers = participants.filter((p) => p.pieceId !== winner.pieceId);
  const chance = Math.round((winner.probability ?? 0) * 100);
  return {
    title:
      losers.length === 1
        ? `${name(winner.pieceId)} beat ${losers[0]!.name}`
        : `${name(winner.pieceId)} won the clash`,
    note: chance < 50 ? `Against the odds: a ${chance}% chance` : `A ${chance}% chance`,
  };
}

/** How far the telling has got, for a view: which supports are down, whether it rolls or is over. */
export function storyMoment(story: CardStory | undefined) {
  if (!story) return { placed: Infinity, rolling: false, over: true };
  const n = story.order.length;
  return {
    placed: Math.min(story.step, n),
    rolling: story.step === rollStep(story),
    over: story.step >= storyEnd(story),
  };
}

/** Each fighter's chance as the telling stands: after the supports down so far. */
export function chanceNow(story: CardStory | undefined, pieceId: number): number | undefined {
  if (!story) return undefined;
  const i = story.order.indexOf(pieceId);
  return i < 0 ? undefined : story.odds[Math.min(story.step, story.order.length)]![i];
}

/** The needle's run along the bar: end to end, twice, slowing, to stop on the draw. */
export function sweep(u: number, draw: number): number {
  const stops = [0, 1, 0.1, 0.9, draw];
  const at = [0, 0.3, 0.55, 0.78, 1];
  const k = Math.max(
    0,
    at.findIndex((a, i) => u <= (at[i + 1] ?? 1) && u >= a),
  );
  const span = (at[k + 1] ?? 1) - at[k]!;
  const f = span > 0 ? (u - at[k]!) / span : 1;
  const eased = (1 - Math.cos(Math.PI * Math.min(1, Math.max(0, f)))) / 2;
  const from = stops[k]!;
  const to = stops[k + 1] ?? draw;
  return from + (to - from) * eased;
}

/** What the telling says at its step, for the screen and the headset alike. */
export function storyNarration(
  ctx: GameContext,
  participants: readonly { pieceId: PieceId; name: string }[],
  scores: readonly CardScore[],
  story: CardStory | undefined,
): string {
  const moment = storyMoment(story);
  if (!story || moment.over) return '';
  if (moment.rolling) return 'The chances are rolled…';
  if (story.step === 0) return 'The supports are laid down…';
  const id = story.order[story.step - 1];
  const score = scores.find((s) => s.pieceId === id);
  const name = participants.find((p) => p.pieceId === id)?.name ?? '';
  return score?.cardId
    ? `${name} plays ${supportCardName(ctx, score.cardId)}`
    : `${name} plays no support`;
}
